'use client';

import Image from 'next/image';
import { useEffect, useId, useMemo, useState } from 'react';

import PriceDetails from '@/components/global/PriceDetails';
import { usePricing } from '@/contexts/PricingContext';
import { useProduct } from '@/contexts/ProductContext';
import { recoverFromStaleServerAction } from '@/lib/errors/staleServerAction';
import { getProductByID } from '@/lib/shopify';
import { toGid } from '@/lib/shopify/gid';
import { buildContentstackImageUrl } from '@/lib/utils';
import rte, { rteHasContent } from '@/utils/rte';
import { findMatchingVariant, formatPrice } from '@/utils/utilities.mjs';

// BL-701: the V2 cross sell group. The data plumbing is deliberately the same
// as the V1 block in components/blocks/crossSell — Shopify products are fetched
// client-side (so buy_box needs no entry in enrichBlocksWithShopifyData), the
// variant is matched to the main product's size, and selections are pushed into
// ProductContext.additionalProducts so Add to Cart picks them up.
//
// What changes is the presentation. V1 is a thumbnail on the right of a compact
// row; V2 is a bordered card with a full-bleed product shot on top. Geometry
// below is measured off the reference screenshot on the ticket (a 532px-wide
// 2x capture): card image 486x158 => aspect 3.08, info row 88px tall with 24px
// of vertical padding around a 40px two-line content box, 28px checkbox, 16px
// horizontal padding and gap.

const RETRY_DELAY_MS = 1000;

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="size-4"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CardSkeleton = () => (
  <div
    className="relative overflow-hidden bg-white ring-1 ring-sand ring-inset"
    aria-busy="true"
    aria-label="Loading add-on option"
  >
    <div className="h-50 w-full animate-pulse bg-sand-light" />
    <div className="flex animate-pulse items-start gap-4 p-4">
      <div className="size-7 shrink-0 border-2 border-sand-light bg-white" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="h-4 w-40 rounded bg-sand-light" />
        <div className="h-3 w-16 rounded bg-sand-light/80" />
      </div>
      <div className="h-4 w-16 shrink-0 rounded bg-sand-light" />
    </div>
  </div>
);

function CrossSellCard({ item, variant, isChecked, onToggle }) {
  const { variant: getVariantPrices } = usePricing();
  const labelId = useId();

  const prices = getVariantPrices(variant);
  const sale = Number.isFinite(prices.sale)
    ? prices.sale
    : parseFloat(variant.price?.amount ?? 0);
  const full = Number.isFinite(prices.full) ? prices.full : sale;
  const showStrike = prices.hasPriceDifference && full > sale;
  const isSoldOut = !variant.availableForSale;

  return (
    <label
      className={`relative block overflow-hidden bg-white ${
        isSoldOut ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
      }`}
    >
      {item.image && (
        <span className="relative block h-50 w-full">
          <Image
            src={item.image}
            // BL-514: described when the asset has a description, decorative
            // (next to its label, see V2Usp) when it doesn't.
            alt={item.imageAlt}
            role={item.imageAlt ? undefined : 'presentation'}
            fill
            sizes="(min-width: 1024px) 500px, 100vw"
            className="object-cover object-center"
          />
        </span>
      )}

      {/* The input has to stay a direct sibling of the box it styles — Tailwind's
          peer-checked: compiles to `~`, which only reaches following siblings. */}
      <span className="flex items-start gap-4 p-4">
        <input
          type="checkbox"
          className="peer sr-only"
          autoComplete="off"
          value={variant.id}
          // The label wraps the image too, so without this its alt would be
          // read out as part of the checkbox's name.
          aria-labelledby={`${labelId}-title ${labelId}-variant ${labelId}-price`}
          checked={isChecked}
          onChange={onToggle}
          disabled={isSoldOut}
        />
        <span
          aria-hidden="true"
          className="flex size-7 shrink-0 items-center justify-center border-2 border-sand bg-white text-transparent peer-checked:border-sea peer-checked:bg-sea peer-checked:text-white peer-focus-visible:ring-2 peer-focus-visible:ring-sea peer-focus-visible:ring-offset-2"
        >
          <CheckIcon />
        </span>

        <span className="flex min-w-0 flex-1 flex-col">
          <span id={`${labelId}-title`} className="text-base leading-5 font-medium">
            {item.productTitle}
          </span>
          <span id={`${labelId}-variant`} className="text-primary text-sm leading-5">
            {variant.title}
            {isSoldOut && ' (Out of stock)'}
          </span>
        </span>

        <span id={`${labelId}-price`} className="flex shrink-0 flex-col text-right">
          {sale > 0 && (
            <span className="text-base leading-5 font-bold">
              +{formatPrice(sale)}
            </span>
          )}
          {showStrike && (
            <s className="text-primary text-sm leading-5 font-light">
              {formatPrice(full)}
            </s>
          )}
        </span>
      </span>

      {/* Overlay rather than a border/ring on the label itself — the product
          image would otherwise paint over an inset ring and hide it. */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 ring-inset ${
          isChecked ? 'ring-[2.5px] ring-sea' : 'ring-1 ring-sand'
        }`}
      />
    </label>
  );
}

const V2CrossSellGroup = ({ block }) => {
  const { section_heading, description, products } = block;

  const { selectedVariant: mainSelectedVariant, setAdditionalProducts } =
    useProduct();

  const [rawShopifyProducts, setRawShopifyProducts] = useState([]);
  const [selectedUids, setSelectedUids] = useState([]);
  const [loading, setLoading] = useState(true);

  // `products` is a multiple group whose only field is a reference, so each row
  // arrives as { product: [entry] }. The fallbacks mirror V1's normalization.
  const items = useMemo(() => {
    return (products || [])
      .map((row) => {
        const entry = row?.product?.[0] || row;

        return {
          uid:
            row?._metadata?.uid ??
            row?._uid ??
            entry?.uid ??
            entry?.shopify_product_id,
          shopifyProductId: entry?.shopify_product_id,
          productTitle: entry?.product_name || entry?.title,
          cmsImage: entry?.buy_box_add_on_settings?.product_image?.url || null,
          cmsImageAlt:
            entry?.buy_box_add_on_settings?.product_image?.description || '',
        };
      })
      .filter((item) => item.shopifyProductId);
  }, [products]);

  useEffect(() => {
    let isMounted = true;

    async function init() {
      if (items.length === 0) {
        if (isMounted) setLoading(false);
        return;
      }

      const results = await Promise.all(
        items.map(async (item) => {
          const productGid = toGid('Product', item.shopifyProductId);
          const context = {
            stage: 'v2_cross_sell_group',
            productId: item.shopifyProductId,
          };

          // BL-996: nearly all failures here are transient network errors
          // (iOS Safari "Load failed"), so one retry before dropping the card.
          // A stale Server Action would fail the same way again — skip it.
          try {
            const product = await getProductByID(productGid);
            return product ? { ...item, product } : null;
          } catch (error) {
            if (recoverFromStaleServerAction(error, context)) return null;
          }

          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
          if (!isMounted) return null;

          try {
            const product = await getProductByID(productGid);
            return product ? { ...item, product } : null;
          } catch (error) {
            console.error(
              'Failed to load V2 cross-sell product',
              item.shopifyProductId,
              error,
            );
            recoverFromStaleServerAction(error, context);
            return null;
          }
        }),
      );

      if (isMounted) {
        setRawShopifyProducts(results.filter(Boolean));
        setLoading(false);
      }
    }

    init();
    return () => {
      isMounted = false;
    };
    // BL-996: fetched once. The product already carries every variant with its
    // availableForSale, and `cards` picks the one for the chosen size, so a
    // size change needs no new request.
  }, [items]);

  const cards = useMemo(() => {
    return rawShopifyProducts
      .map((item) => {
        const variant = findMatchingVariant(mainSelectedVariant, item.product);
        if (!variant) return null;

        const shopifyImage = variant.image || item.product.featuredImage || null;

        return {
          ...item,
          image: item.cmsImage
            ? buildContentstackImageUrl(item.cmsImage, 768)
            : shopifyImage?.url || null,
          imageAlt: item.cmsImage
            ? item.cmsImageAlt
            : shopifyImage?.altText || '',
          variant,
        };
      })
      .filter(Boolean);
  }, [rawShopifyProducts, mainSelectedVariant]);

  const selectedCards = useMemo(() => {
    return selectedUids
      .map((uid) => cards.find((card) => card.uid === uid))
      .filter((card) => card?.variant?.availableForSale === true);
  }, [selectedUids, cards]);

  // Keyed on the CMS uid rather than the variant id so a size change keeps the
  // selection. `_sourceUid` is what lets this block prune only its own rows.
  useEffect(() => {
    const scopeUids = cards.map((card) => card.uid).filter(Boolean);

    setAdditionalProducts((prev) => {
      const unrelated = prev.filter(
        (entry) => !scopeUids.includes(entry._sourceUid),
      );

      const selected = selectedCards.map((card) => ({
        ...card.variant,
        title: card.productTitle,
        variantTitle: card.variant.title,
        _sourceUid: card.uid,
      }));

      const next = [...unrelated, ...selected];

      return prev.length === next.length &&
        prev.every((entry, i) => entry.id === next[i].id)
        ? prev
        : next;
    });
  }, [selectedCards, cards, setAdditionalProducts]);

  function toggle(uid) {
    setSelectedUids((prev) =>
      prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid],
    );
  }

  if (items.length === 0) return null;

  return (
    <div
      data-block="v2_cross_sell_group"
      className="font-worksans text-primary py-4"
    >
      {/* 20px/500, matching the buy box's other section headings ("Mattress
          Size.", "Sleep Hot?") per Figma (node 2014:67, style_8280743f). */}
      {section_heading && (
        <p className="text-xl leading-5 font-medium">{section_heading}</p>
      )}

      {rteHasContent(description) && (
        <div
          className="rte-content mt-2 font-light leading-6 [&_a]:font-semibold"
          dangerouslySetInnerHTML={{ __html: rte(description) }}
        />
      )}

      <div className="mt-4 flex flex-col gap-4">
        {loading
          ? items.map((item) => <CardSkeleton key={item.uid} />)
          : cards.map((card) => (
              <CrossSellCard
                key={card.uid}
                item={card}
                variant={card.variant}
                isChecked={selectedCards.some((s) => s.uid === card.uid)}
                onToggle={() => toggle(card.uid)}
              />
            ))}
      </div>

      <PriceDetails className="mt-5" />
    </div>
  );
};

export default V2CrossSellGroup;
